import './index.less';
import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/iconfont/Icon.jsx';
import { FILE_TYPE } from '@/store/slices/recording.js';
import { assetController } from '@/api/index.js';
import { timeDiffFromNow } from '@/utils/dayjs.js';
import EmptyLogo from '@/assets/assets/video.png';
import UploadingLogo from '@/assets/assets/slide-file.png';
import { useTranslation } from 'react-i18next';

export default function Assets({ show, onSelect, onClose }) {
  const dialogRef = useRef(null);
  const [selectedTab, setSelectedTab] = useState(FILE_TYPE.VIDEO);
  const [selectedAsset, setSelectedAsset] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [videoList, setVideoList] = useState([]);
  const [slideList, setSlideList] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    if (show) {
      setSelectedTab(FILE_TYPE.VIDEO);
      setSelectedAsset({});
      dialogRef.current.show();
      dialogRef.current.addEventListener(
        'close',
        () => {
          onClose && onClose();
        },
        {
          once: true,
        },
      );
      setLoading(false);
      assetController
        .assetsList()
        .then((res) => {
          if (res.code === 0) {
            setVideoList(res.data.videos || []);
            setSlideList(res.data.slices || []);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [show]);

  function handleClickAsset(item) {
    if (item.status !== 0) {
      setSelectedAsset(item);
    }
  }

  let videoContent = (
    <div className="empty">
      {isLoading ? (
        <span className="loading loading-spinner loading-lg"></span>
      ) : (
        <div className="tip">
          <img src={EmptyLogo} alt="" />
          <span>{t('As_Recording_NoVideos')}</span>
        </div>
      )}
    </div>
  );
  if (videoList.length > 0) {
    videoContent = videoList.map((item) => {
      return (
        <div
          key={item.id}
          className={`asset-item ${selectedAsset.id === item.id ? 'selected' : ''}`}
          onClick={() => handleClickAsset(item)}>
          <div className="thumbnail-wrapper">
            <video src={item.url} muted />
          </div>

          <div className="info">
            <span>{item.name}</span>

            <span>
              {t('As_Recording_LastTime')} {timeDiffFromNow(item.gmtCreated)}
            </span>
          </div>
        </div>
      );
    });
  }

  let slideContent = (
    <div className="empty">
      {isLoading ? (
        <span className="loading loading-spinner loading-lg"></span>
      ) : (
        <div className="tip">
          <img src={EmptyLogo} alt="" />
          <span>{t('As_Recording_NoSlides')}</span>
        </div>
      )}
    </div>
  );
  if (slideList.length > 0) {
    slideContent = slideList.map((item) => {
      return (
        <div
          key={item.id}
          className={`asset-item ${selectedAsset.id === item.id ? 'selected' : ''}`}
          onClick={() => handleClickAsset(item)}>
          <div className="thumbnail-wrapper">
            {item.status === 0 ? (
              <div className="unavailable">
                <img src={UploadingLogo} alt="" />
                <span>{t('As_Recording_Uploading')}&nbsp;</span>
                <span className="loading loading-dots loading-xs"></span>
              </div>
            ) : (
              <img src={item.thumbnail} />
            )}
          </div>

          <div className="info">
            <span>{item.name}</span>

            <span>
              {t('As_Recording_LastTime')} {timeDiffFromNow(item.gmtCreated)}
            </span>
          </div>
        </div>
      );
    });
  }

  async function selectAsset() {
    if (selectedTab === FILE_TYPE.VIDEO) {
      if (onSelect) {
        onSelect({
          videoUrl: selectedAsset.url,
        });
      }
    } else if (selectedTab === FILE_TYPE.SLIDE) {
      const res = await assetController.detail({ assetId: selectedAsset.id });
      if (res.code === 0) {
        onSelect({
          slideUrls: res.data.map(({ url }) => url),
        });
      }
    }
    dialogRef.current.close();
  }

  return (
    <div className="record-assets-list">
      <dialog ref={dialogRef} className="modal">
        <div className="modal-box">
          <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2 close_icon_btn">
              <Icon name="icon-close-stroke" />
            </button>
          </form>

          <h3 className="font-bold text-lg">{t('As_Recording_Assets1')}</h3>

          <section>
            <div className="tab-btn">
              <button
                onClick={() => {
                  setSelectedTab(FILE_TYPE.VIDEO);
                }}
                className={`btn btn-ghost ${selectedTab === FILE_TYPE.VIDEO ? 'selected' : ''}`}>
                {t('As_Recording_Assets2')}
              </button>

              <button
                onClick={() => {
                  setSelectedTab(FILE_TYPE.SLIDE);
                }}
                className={`btn btn-ghost ${selectedTab === FILE_TYPE.SLIDE ? 'selected' : ''}`}>
                {t('As_Recording_Assets3')}
              </button>
            </div>

            <div className="asset-list">
              {selectedTab === FILE_TYPE.VIDEO ? videoContent : slideContent}
            </div>
          </section>

          <div className="footer-action">
            <button className="btn btn-primary" onClick={selectAsset} disabled={!selectedAsset.id}>
              {t('As_Recording_AssetsSelect')}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
